<template>
    <v-autocomplete
        v-model="airportCode"
        :items="items"
        :loading="loading"
        hide-no-data
        hide-selected
        item-value="iataCode"
        item-title="iataCode"
        :label="label"
        no-filter
        clearable
        :rules="rules"
        :disabled="disabled"
        :outlined="outlined"
        :hide-details="hideDetails"
        :single-line="singleLine"
        :density="dense ? 'compact' : 'comfortable'"
        @update:model-value="$emit('change', $event ? $event.iataCode : null)"
        @update:search="onSearch"
    >
        <template v-slot:item="{item, props}">
            <v-list-item v-bind="props">
                <template v-slot:title>
                    <v-icon size="small" start>mdi-airplane-takeoff</v-icon>
                    {{ itemText ? `${item.raw[itemText]}` : `${item.raw.iataCode} - ${item.raw.name}` }}
                </template>
            </v-list-item>
        </template>
        <template v-slot:selection="{item}">
            <div class="full-width full-height d-flex">
                <span style="line-height: 1.2rem">
                    {{ itemText ? `${item.raw[itemText]}` : `${item.raw.iataCode} - ${item.raw.name}` }}
                </span>
            </div>
        </template>
        <template v-slot:details>
            <span v-if="selectedAirport" class="mb-1">
                {{ `${selectedAirport.cityName}, ${selectedAirport.countryName}` }}
            </span>
        </template>
    </v-autocomplete>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import {AirportsSearchMixin} from '~/mixins/AirportsSearchMixin.vue'
    import {LoadMixin} from '@/mixins/LoadMixin.vue'

    @Component
    export class AirportsSearchInput extends mixins(AirportsSearchMixin, LoadMixin) {
        @VModel({default: null}) airportCode
        @Prop() rules
        @Prop() label
        @Prop({default: false, type: Boolean}) disabled
        @Prop({default: false, type: Boolean}) outlined
        @Prop({default: false, type: Boolean}) hideDetails
        @Prop({default: false, type: Boolean}) singleLine
        @Prop({default: false, type: Boolean}) dense
        @Prop({default: null}) itemText

        items = []
        search = null
        loading = false
        timer = null

        onSearch(val) {
            this.search = val
            if (!val) return
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.timer = window.setTimeout(() => {
                this.initSearch({
                    pattern: val,
                })
            }, 400)
        }

        @Watch('airportCode', {immediate: true})
        async onAirportCodeChange() {
            if (this.airportCode && !this.items.find(a => a.iataCode === this.airportCode)) {
                await this.preloadAirport()
            }
        }

        async preloadAirport() {
            if (this.airportCode) {
                const airportCode = this.airportCode
                this.airportCode = null
                try {
                    this.loading = true
                    const [airport] = await this.$api.airports.get({iataCode: airportCode})
                    this.items = [Object.assign(airport, {parentName: airport.countryName, type: 'airport'})]
                    // eslint-disable-next-line no-empty
                } catch (e) {
                } finally {
                    this.airportCode = airportCode
                    this.loading = false
                }
            }
        }

        async initSearch(val) {
            this.loading = true
            try {
                this.items = await this.searchAirports(val)
            } finally {
                this.loading = false
            }
        }

        async load() {
            if (!this.airportCode) return
            try {
                this.loading = true
                this.items = await this.searchAirports({
                    iataCode: this.airportCode,
                })
                this.airportCode = this.items[0]?.iataCode
            } finally {
                this.loading = false
            }
        }

        get selectedAirport() {
            return this.items.find(i => i.iataCode === this.airportCode)
        }
    }

    export default AirportsSearchInput
</script>

<style scoped lang="scss">
    :deep(.v-autocomplete__selection) {
        line-height: unset !important;
        height: unset !important;
        display: flex;
        align-items: start;
    }
</style>
